
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Music, MessageSquare, Zap, TrendingUp, CheckCircle, DollarSign, Shield, Award } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function HowItWorks() {
  const navigate = useNavigate();

  useEffect(() => {
    // Add comprehensive SEO meta tags
    document.title = "How It Works - Soundope 2025 | Real Feedback, Real Playlists, Real Money";
    
    const metaTags = [
      { type: 'name', key: 'description', content: "Upload once, get real feedback from real people. Submit to playlists FREE or from $1. Cheaper than SubmitHub, Groover, Playlist Push. No bots, no fake streams." },
      { type: 'name', key: 'keywords', content: "how soundope works, music feedback, playlist submission, earn credits, sell music, black wings elite, independent artist" },
      { type: 'name', key: 'robots', content: 'index, follow' },
      
      // Open Graph
      { type: 'property', key: 'og:title', content: 'How It Works - Soundope 2025' },
      { type: 'property', key: 'og:description', content: 'Real feedback, real playlists, real money. No bots, no fake streams.' },
      { type: 'property', key: 'og:type', content: 'website' },
      { type: 'property', key: 'og:url', content: window.location.href },
      
      // Twitter
      { type: 'name', key: 'twitter:card', content: 'summary_large_image' },
      { type: 'name', key: 'twitter:title', content: 'How Soundope Works' },
      { type: 'name', key: 'twitter:description', content: 'Upload → real listeners → real feedback → real playlists → real money' },
      
      // Canonical URL
      { type: 'link', key: 'canonical', content: window.location.origin + window.location.pathname },
    ];

    const addedElements = [];

    metaTags.forEach(({ type, key, content }) => {
      if (type === 'link') {
        let tag = document.querySelector(`link[rel="${key}"]`);
        if (tag) {
          tag.setAttribute('href', content);
        } else {
          tag = document.createElement('link');
          tag.setAttribute('rel', key);
          tag.setAttribute('href', content);
          document.head.appendChild(tag);
          addedElements.push(tag);
        }
      } else {
        let tag = document.querySelector(`meta[${type}="${key}"]`);
        if (tag) {
          tag.setAttribute('content', content);
        } else {
          tag = document.createElement('meta');
          tag.setAttribute(type, key);
          tag.setAttribute('content', content);
          document.head.appendChild(tag);
          addedElements.push(tag);
        }
      }
    });

    return () => {
      addedElements.forEach(tag => {
        if (tag.parentNode) {
          tag.parentNode.removeChild(tag);
        }
      });
    };
  }, []);

  return (
    <div className="min-h-screen px-4 py-16 pb-32">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-6xl font-light mb-8 text-[#d0d0d0]">
            How It Works – Soundope 2025
          </h1>
        </div>

        {/* Main Content Sections */}
        <div className="space-y-12 mb-16">
          {/* Upload Your Track */}
          <div className="neuro-base rounded-3xl p-8">
            <h2 className="text-2xl md:text-3xl font-medium text-[#d0d0d0] mb-4">
              Upload Your Track
            </h2>
            <p className="text-base md:text-lg text-[#909090] leading-relaxed">
              Drop your song once. It instantly lands on our Discovery page where real listeners actually press play. No bots, no canvas farms.
            </p>
          </div>

          {/* Get Heard + Get Real Feedback */}
          <div className="neuro-base rounded-3xl p-8">
            <h2 className="text-2xl md:text-3xl font-medium text-[#d0d0d0] mb-4">
              Get Heard + Get Real Feedback
            </h2>
            <div className="text-base md:text-lg text-[#909090] leading-relaxed space-y-3">
              <p>Every play shows live under the player.</p>
              <p>Listeners hit praise (wings) or scathe (burn it).</p>
              <p>You see exactly who loved it and who hated it – brutal, honest, useful.</p>
            </div>
          </div>

          {/* Submit to Real Playlists */}
          <div className="neuro-base rounded-3xl p-8">
            <h2 className="text-2xl md:text-3xl font-medium text-[#d0d0d0] mb-4">
              Submit to Real Playlists (Free or Paid)
            </h2>
            <div className="text-base md:text-lg text-[#909090] leading-relaxed space-y-4">
              <p>
                <strong className="text-[#b0b0b0]">Free submission</strong> → goes straight to our in-house playlists (trap metal, phonk, screamo, niche shit, all genres welcome).
              </p>
              <p>
                <strong className="text-[#b0b0b0]">Paid submission</strong> → starting at $1 you pick the exact curator or playlist tier you want. Up to $4 for the big dogs.
              </p>
              <p className="text-[#a0a0a0]">
                Cheaper than SubmitHub, Groover, Playlist Push, Daily Playlists – and zero fake guarantees.
              </p>
            </div>
          </div>

          {/* Earn Credits or Pay Cash */}
          <div className="neuro-base rounded-3xl p-8">
            <h2 className="text-2xl md:text-3xl font-medium text-[#d0d0d0] mb-4">
              Earn Credits or Pay Cash
            </h2>
            <div className="text-base md:text-lg text-[#909090] leading-relaxed space-y-3">
              <p>Leave good feedback for others → earn credits → use them to boost your own tracks.</p>
              <p>Or just buy credits (still the lowest prices in the game).</p>
            </div>
          </div>

        </div>

        {/* Closing Statement */}
        <div className="neuro-base rounded-3xl p-8 md:p-12 mb-12 text-center">
          <div className="max-w-3xl mx-auto space-y-4">
            <h2 className="text-2xl md:text-3xl font-light text-[#d0d0d0] mb-6">
              That's it.
            </h2>
            <p className="text-lg md:text-xl text-[#b0b0b0] leading-relaxed">
              One upload → real listeners → real feedback → real playlists → real money.
            </p>
            <p className="text-base md:text-lg text-[#909090] leading-relaxed">
              No bots. No $50 "100 playlists" scams. No monkey spank.
            </p>
            <p className="text-base md:text-lg text-[#a0a0a0] leading-relaxed">
              Upload. Get wings or get scathed. Fly or fall.
            </p>
            <p className="text-sm md:text-base text-[#808080] mt-6">
              Dead Entertainment – we put the artist and the curator first.<br />
              Everyone else can wait in line.
            </p>
          </div>
        </div>

        {/* CTA Button */}
        <div className="text-center">
          <Button
            onClick={() => navigate(createPageUrl("Upload"))}
            className="neuro-base active:neuro-pressed rounded-3xl px-16 py-8 text-xl smooth-transition hover:scale-105"
            tooltip="Upload your track to get started with free feedback and playlist submissions"
          >
            <span className="text-[#b0b0b0]">Upload Track Now</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
